/**
 * Felder des Container-Formulars und ihre Reiter (REQ-27 §2.2).
 *
 * Jeder Eintrag von {@link containerFieldRenderers} zeichnet genau ein Feld aus
 * `IwmFormContainer.razor` — gleicher Baustein, gleiche Beschriftung wie bisher. Die Reiter
 * nennen nur Feldnamen; Reihenfolge und Zuordnung stehen damit an einer Stelle.
 */

import type { ReactNode } from 'react';

import type { Container } from '@/types/entities';
import { EntityTypes } from '@/types/enums';

import { CheckBoxField } from './fields/CheckBoxField';
import { DateField } from './fields/DateField';
import { GruppenkennzeichenField } from './fields/GruppenkennzeichenField';
import { TextField } from './fields/TextField';
import type { ViewForm } from './useViewForm';

/** Alle Auswahllisten des Formulars gehören zur Entität `Container`. */
const ENTITY = EntityTypes.Container;

type ContainerFieldName = keyof Container;

type ContainerFieldRenderer = (form: ViewForm<Container>) => ReactNode;

const text =
  (name: ContainerFieldName, label: string): ContainerFieldRenderer =>
  (form) => <TextField form={form} name={name} label={label} />;

const date =
  (name: ContainerFieldName, label: string): ContainerFieldRenderer =>
  (form) => <DateField form={form} name={name} label={label} />;

const flag =
  (name: ContainerFieldName, label: string): ContainerFieldRenderer =>
  (form) => <CheckBoxField form={form} name={name} label={label} />;

/**
 * Auswahlliste aus den Gruppenkennzeichen. `fieldName` ist der Feldname der API; er weicht
 * bei `NlSp` → `NLSp` und `PoD` → `Pod` vom Modellfeld ab.
 */
const lookup =
  (name: ContainerFieldName, label: string, fieldName: string = name): ContainerFieldRenderer =>
  (form) => (
    <GruppenkennzeichenField
      form={form}
      name={name}
      entity={ENTITY}
      fieldName={fieldName}
      label={label}
    />
  );

export const containerFieldRenderers = {
  ContainerNo: text('ContainerNo', 'Containernummer'),
  ContTyp: lookup('ContTyp', 'Typ'),
  Plombe: text('Plombe', 'Plombe'),
  BlNummer: text('BlNummer', 'BlNummer'),
  Empfaenger: lookup('Empfaenger', 'Empfänger'),

  Etd: date('Etd', 'ETD'),
  Eta: date('Eta', 'ETA'),
  Reeder: lookup('Reeder', 'Reeder'),
  Schiff: text('Schiff', 'Schiff'),
  PoD: lookup('PoD', 'PoD', 'Pod'),
  Spediteur: lookup('Spediteur', 'Spediteur'),

  FruehestAnlieferdatum: date('FruehestAnlieferdatum', 'Frühestes Anlieferdatum'),
  Anlieferdatum: date('Anlieferdatum', 'Anlieferdatum'),
  AnlieferdatumEmpf: date('AnlieferdatumEmpf', 'Anlieferdatum Empf.'),
  Anlieferuhrzeit: lookup('Anlieferuhrzeit', 'Anlieferuhrzeit'),
  TerminBestaetigungCif: flag('TerminBestaetigungCif', 'Terminbestätigung CIF'),

  EingangErledigt: flag('EingangErledigt', 'Eingang erledigt'),
  DatumZugestellt: date('DatumZugestellt', 'Datum Zugestellt'),
  DepotName: text('DepotName', 'Depotname'),
  DepotDatum: date('DepotDatum', 'Depotdatum'),
  Containerleerrueckgabe: text('Containerleerrueckgabe', 'Containerleerrückgabe'),
  RueckgabeReferenz: text('RueckgabeReferenz', 'Rückgabereferenz'),
  NlSp: lookup('NlSp', 'NlSp', 'NLSp'),
  NlSpPos: text('NlSpPos', 'NlSpPos'),
  Bemerkung: text('Bemerkung', 'Bemerkung'),
} satisfies Partial<Record<ContainerFieldName, ContainerFieldRenderer>>;

/** Feldname, zu dem es einen Baustein gibt — die Reiter dürfen nur diese nennen. */
export type ContainerFormField = keyof typeof containerFieldRenderers;

export interface ContainerTab {
  id: string;
  label: string;
  /** Felder in Anzeigereihenfolge. */
  fields: readonly ContainerFormField[];
}

/** Reiter mit Feldern; die lesenden Reiter „Positionen" und „Rechnungen" kommen dazu. */
export const containerTabs: readonly ContainerTab[] = [
  {
    id: 'stammdaten',
    label: 'Stammdaten',
    fields: ['ContainerNo', 'ContTyp', 'Plombe', 'BlNummer', 'Empfaenger'],
  },
  {
    id: 'route',
    label: 'Route',
    fields: ['Etd', 'Eta', 'Reeder', 'Schiff', 'PoD', 'Spediteur'],
  },
  {
    id: 'anlieferung',
    label: 'Anlieferung',
    fields: [
      'FruehestAnlieferdatum',
      'Anlieferdatum',
      'AnlieferdatumEmpf',
      'Anlieferuhrzeit',
      'TerminBestaetigungCif',
    ],
  },
  {
    id: 'abwicklung',
    label: 'Abwicklung',
    fields: [
      'EingangErledigt',
      'DatumZugestellt',
      'DepotName',
      'DepotDatum',
      'Containerleerrueckgabe',
      'RueckgabeReferenz',
      'NlSp',
      'NlSpPos',
      'Bemerkung',
    ],
  },
];
